import { useQueryClient } from '@tanstack/react-query'
import { AUDIT_LOG_KEYS } from './audit-logs'
import { api } from './client'
import type { paths } from './schema'

// Batches (UI-SCREENS F-04 / F-05 / F-06 / F-07 / F-08, #128, #129, #131). Owner and Staff both
// start, complete, cancel and read them; the Owner voids them, and the note and failure reason
// stay editable (Owner any, Staff own; 04f #132).
type BatchPayload = paths['/api/w/{slug}/batches']['get']['responses'][200]['content']['application/json'][number]
export type BatchSlot = BatchPayload['slots'][number]
export type Batch = BatchPayload
export type BatchDetail = NonNullable<ReturnType<typeof useBatch>['data']>
export type BatchMovement = BatchDetail['movements'][number]
export type BatchBody = NonNullable<paths['/api/w/{slug}/batches']['post']['requestBody']>['content']['application/json']
export type CompletionBody = NonNullable<
  paths['/api/w/{slug}/batches/{batch_id}/completion']['post']['requestBody']
>['content']['application/json']
export type CancellationBody = NonNullable<
  paths['/api/w/{slug}/batches/{batch_id}/cancellation']['post']['requestBody']
>['content']['application/json']
export type BatchEditBody = NonNullable<
  paths['/api/w/{slug}/batches/{id}']['patch']['requestBody']
>['content']['application/json']
export type BatchFilters = NonNullable<paths['/api/w/{slug}/batches']['get']['parameters']['query']>

// The seven stored failure reasons (DATA-MODEL §4); their labels live in id.json.
export const FAILURE_REASONS = ['adhesion', 'spaghetti', 'layer_shift', 'clog', 'warping', 'quality', 'other'] as const

// The four stored cancel reasons (DATA-MODEL §4); their labels live in id.json.
export const CANCEL_REASONS = ['wrong_file_or_color', 'machine_needed', 'power_outage', 'other'] as const

export const PURPOSES = ['production', 'prototype', 'calibration', 'personal'] as const
export const STATUSES = ['printing', 'completed', 'cancelled'] as const

const LIST = ['get', '/api/w/{slug}/batches'] as const
const BATCH = ['get', '/api/w/{slug}/batches/{id}'] as const

export function useBatches(slug: string, filters: BatchFilters = {}) {
  return api.useQuery('get', '/api/w/{slug}/batches', { params: { path: { slug }, query: filters } }, { retry: false })
}

export function useBatch(slug: string, id: number) {
  return api.useQuery('get', '/api/w/{slug}/batches/{id}', { params: { path: { slug, id } } }, { retry: false })
}

// A start makes the Machine "printing" and its Pools "in use", so those are refetched too. A Batch
// recorded after the fact (04d #130) posts the Pool deductions and WIP parts as well.
export function useStartBatch() {
  const queryClient = useQueryClient()
  return api.useMutation('post', '/api/w/{slug}/batches', {
    onSettled: async () => {
      await Promise.all(
        [
          LIST,
          BATCH,
          ['get', '/api/w/{slug}/machines'],
          ['get', '/api/w/{slug}/machines/{id}'],
          ['get', '/api/w/{slug}/pools'],
          ['get', '/api/w/{slug}/pools/{id}'],
          ['get', '/api/w/{slug}/pools/{pool_id}/movements'],
          ['get', '/api/w/{slug}/variants/{variant_id}/buildable'],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })),
      )
    },
  })
}

// Completing posts the Pool deductions and the WIP parts, and frees the Machine, so the Pools and
// Machines are refetched too.
export function useCompleteBatch() {
  const queryClient = useQueryClient()
  return api.useMutation('post', '/api/w/{slug}/batches/{batch_id}/completion', {
    onSettled: async () => {
      await Promise.all(
        [
          LIST,
          BATCH,
          ['get', '/api/w/{slug}/machines'],
          ['get', '/api/w/{slug}/machines/{id}'],
          ['get', '/api/w/{slug}/pools'],
          ['get', '/api/w/{slug}/pools/{id}'],
          ['get', '/api/w/{slug}/pools/{pool_id}/movements'],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })),
      )
    },
  })
}

// Cancelling posts the wasted grams, if any, as Pool deductions and frees the Machine, so the Pools
// and Machines are refetched too.
export function useCancelBatch() {
  const queryClient = useQueryClient()
  return api.useMutation('post', '/api/w/{slug}/batches/{batch_id}/cancellation', {
    onSettled: async () => {
      await Promise.all(
        [
          LIST,
          BATCH,
          ['get', '/api/w/{slug}/machines'],
          ['get', '/api/w/{slug}/machines/{id}'],
          ['get', '/api/w/{slug}/pools'],
          ['get', '/api/w/{slug}/pools/{id}'],
          ['get', '/api/w/{slug}/pools/{pool_id}/movements'],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })),
      )
    },
  })
}

// A void reverses every movement the Batch posted: the Pools and WIP change, so they are
// refetched with the Batch (04f #132).
export function useVoidBatch() {
  const queryClient = useQueryClient()
  return api.useMutation('post', '/api/w/{slug}/batches/{batch_id}/void', {
    onSettled: async () => {
      await Promise.all(
        [
          LIST,
          BATCH,
          AUDIT_LOG_KEYS.batches,
          ['get', '/api/w/{slug}/pools'],
          ['get', '/api/w/{slug}/pools/{id}'],
          ['get', '/api/w/{slug}/pools/{pool_id}/movements'],
          ['get', '/api/w/{slug}/variants/{variant_id}/buildable'],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })),
      )
    },
  })
}

// The note and the failure reason (#126 Q11); no stock moves, so only the Batch and the list.
export function useUpdateBatch() {
  const queryClient = useQueryClient()
  return api.useMutation('patch', '/api/w/{slug}/batches/{id}', {
    onSettled: async () => {
      await Promise.all(
        [LIST, BATCH, AUDIT_LOG_KEYS.batches].map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })),
      )
    },
  })
}
